import { useEffect, useState } from "react";
import { AndroidVerifyReminder } from "@/components/market/Plans";
import { openTopLevel } from "@/lib/topNavigation";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Loader2, LogIn, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHero } from "@/components/layout/PageHero";
import { GoogleIcon } from "@/pages/AdminLogin";
import { useDevAuth } from "@/context/DevAuthContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import NotFound from "@/pages/NotFound";
import { translateError } from "@/i18n/errors";
import { StripeInfo } from "@/components/market/StripeInfo";

const Field = ({ id, label, testId, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} required className="h-12 bg-black/50" data-testid={testId} {...props} />
  </div>
);

export default function DevAuth() {
  const { t } = useI18n();
  const { dev, enter } = useDevAuth();
  const { setUser } = useAuth();
  const { marketplace } = useSite();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "signup" ? "signup" : "login";
  const mini = params.get("type") === "minisite";  // « Client mini-site » account (no sandbox / APK hosting)
  const [form, setForm] = useState({ name: "", brand: "", email: params.get("email") || "", password: "" });  // deep link: email prefilled
  const [error, setError] = useState(location.state?.error ? t(location.state.error) : "");
  const [loading, setLoading] = useState(false);
  const [next] = useState(() => sessionStorage.getItem("fmo_after_login") || "/espace-developpeur");
  useEffect(() => {
    if (dev) sessionStorage.removeItem("fmo_after_login");
  }, [dev]);

  if (!marketplace) return <NotFound />;
  if (dev) return <Navigate to={next} replace />;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const plan = ["standard", "pro", "ultra"].includes(params.get("plan")) ? params.get("plan") : "standard";
      const body = mode === "signup" ? { ...form, plan, kind: mini ? "minisite" : "developer" } : { email: form.email, password: form.password, kind: mini ? "minisite" : "developer" };
      const res = await enter(mode === "signup" ? "/dev/auth/register" : "/dev/auth/login", body);
      if (res?.admin) {
        setUser(res.user);
        navigate("/admin", { replace: true });
        return;
      }
      navigate(next, { replace: true });
    } catch (err) {
      setError(translateError(err, t));
      if (mode === "signup" && err?.response?.status === 403) setParams({});  // admin email → switch to the Connexion tab
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("fmo_signup_kind", mini ? "minisite" : "developer");
    const redirectUrl = window.location.origin + "/espace-developpeur/connexion";
    openTopLevel(`https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
  };

  const tab = (m, Icon) => (
    <button type="button" onClick={() => { setError(""); setParams({ ...(m === "signup" && { mode: "signup" }), ...(mini && { type: "minisite" }) }); }}
      className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full px-2 py-2 text-[0.68rem] font-bold uppercase tracking-wide sm:gap-2 sm:px-4 sm:text-xs sm:tracking-wider transition-colors duration-200 ${mode === m ? "bg-[var(--gold)] text-black" : "text-neutral-300 hover:text-white"}`}
      data-testid={`dev-auth-tab-${m}`}>
      <Icon className="h-4 w-4" /> {t(`devAuth.tab.${m}`)}
    </button>
  );

  return (
    <div data-testid="dev-auth-page">
      <PageHero eyebrow={t(mini ? "client.eyebrow" : "devAuth.eyebrow")} title={t(mini ? (mode === "signup" ? "client.signupTitle" : "client.loginTitle") : mode === "signup" ? "devAuth.signupTitle" : "devAuth.loginTitle")} />
      <section className="mx-auto max-w-md px-4 sm:px-0">
        {mode === "signup" && !mini && <div className="mb-5"><StripeInfo testId="dev-signup-stripe-info" /></div>}
        <form onSubmit={submit} className="card-carbon fade-up space-y-5 rounded-3xl p-6 sm:p-8" data-testid="dev-auth-form">
          <div className="flex gap-1 rounded-full border border-[rgba(245,197,66,0.3)] bg-black/40 p-1">{tab("login", LogIn)}{tab("signup", UserPlus)}</div>
          {error && (
            <div className="flex items-center gap-2 rounded-xl border border-red-500/50 bg-red-950/60 px-4 py-3 text-sm text-red-100" role="alert" data-testid="dev-auth-error">
              <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}
          {mode === "signup" && !mini && <AndroidVerifyReminder />}
          {mini && <p className="break-words text-xs text-emerald-200" data-testid="client-auth-hint">{t("client.authHint")}</p>}
          {mode === "signup" && (
            <>
              <Field id="dev-name" label={t("devAuth.name")} value={form.name} onChange={set("name")} autoComplete="name" testId="dev-signup-name-input" />
              <Field id="dev-brand" label={t(mini ? "client.shop" : "devAuth.brand")} value={form.brand} onChange={set("brand")} autoComplete="organization" testId="dev-signup-brand-input" />
            </>
          )}
          <Field id="dev-email" type="email" label={t("login.email")} value={form.email} onChange={set("email")} autoComplete="email" testId="dev-email-input" />
          <Field id="dev-password" type="password" minLength={mode === "signup" ? 8 : undefined} label={t("login.password")} value={form.password} onChange={set("password")}
            autoComplete={mode === "signup" ? "new-password" : "current-password"} testId="dev-password-input" />
          {mode === "signup" && <p className="-mt-3 text-xs text-neutral-500">{t("devAuth.passwordHint")}</p>}
          <button type="submit" className="btn-red w-full" disabled={loading} data-testid="dev-auth-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "signup" ? <UserPlus className="h-4 w-4" /> : <LogIn className="h-4 w-4" />}
            {t(mode === "signup" ? "devAuth.signupSubmit" : "devAuth.loginSubmit")}
          </button>
          {mode === "login" && (
            <Link to="/espace-developpeur/mot-de-passe-oublie" className="block text-center text-sm text-[var(--gold)] hover:text-[var(--gold-bright)]" data-testid="dev-forgot-link">
              {t("devAuth.forgot")}
            </Link>
          )}
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-neutral-500">
            <span className="h-px flex-1 bg-neutral-700" /> {t("login.or")} <span className="h-px flex-1 bg-neutral-700" />
          </div>
          <button type="button" onClick={google}
            className="flex w-full items-center justify-center gap-3 rounded-full border border-neutral-300 bg-white px-5 py-3 text-sm font-semibold text-neutral-900 transition-transform duration-150 hover:-translate-y-0.5 active:translate-y-0"
            data-testid="dev-google-login-btn">
            <GoogleIcon /> {t("login.google")}
          </button>
        </form>
      </section>
    </div>
  );
}
